import { QuestionType } from "@sp-crm/core";
import { ExpandIndicator } from "components/shared/expand-indicator";
import { Radio, RadioOption } from "components/shared/radio";
import { InlineBanner } from "components/ui/inline-banner";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { BridgeEntityResult, BridgeQuestionInput } from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import {
    candidateDependsOnEditing,
    composeFlatTwoConditionRule,
    decomposeFlatTwoConditionRule,
    isFlatTwoConditionRule,
    LeafRule,
    parseRulesString,
    RuleAst,
    serializeRule,
} from "./rule-helpers";

const BINARY_TYPE = QuestionType[QuestionType.binary];
const NARY_TYPE = QuestionType[QuestionType.nary];

type LeafKind = "binaryvalue" | "naryvalue" | "naryany";

interface LeafDraft {
    kind: LeafKind;
    triggerKey: string;
    optionKey: string;
}

interface RuleDraft {
    join: "and" | "or";
    conditions: LeafDraft[];
}

const emptyDraft: RuleDraft = { join: "and", conditions: [] };

const newCondition: LeafDraft = {
    kind: "binaryvalue",
    triggerKey: "",
    optionKey: "",
};

const naryKindOptions: RadioOption[] = [
    {
        key: "naryany",
        text: "Any option is selected",
    },
    {
        key: "naryvalue",
        text: "A specific option is selected",
    },
];

const joinOptions: RadioOption[] = [
    { key: "and", text: "AND (both conditions must be true)" },
    { key: "or", text: "OR (either condition can be true)" },
];

function leafToDraft(leaf: LeafRule): LeafDraft {
    if (leaf.operator === "binaryvalue") {
        return { kind: "binaryvalue", triggerKey: leaf.question, optionKey: "" };
    }
    if (leaf.operator === "naryany") {
        return { kind: "naryany", triggerKey: leaf.question, optionKey: "" };
    }
    return {
        kind: "naryvalue",
        triggerKey: leaf.question,
        optionKey: leaf.contains,
    };
}

function ruleToDraft(rule: RuleAst): RuleDraft {
    if (isFlatTwoConditionRule(rule)) {
        const parts = decomposeFlatTwoConditionRule(rule);
        return {
            join: parts.join,
            conditions: [leafToDraft(parts.first), leafToDraft(parts.second)],
        };
    }
    if (
        rule.operator === "binaryvalue" ||
        rule.operator === "naryvalue" ||
        rule.operator === "naryany"
    ) {
        return { join: "and", conditions: [leafToDraft(rule)] };
    }
    return emptyDraft;
}

function draftToLeaf(draft: LeafDraft): LeafRule | null {
    if (!draft.triggerKey) {
        return null;
    }
    if (draft.kind === "binaryvalue") {
        return { operator: "binaryvalue", question: draft.triggerKey };
    }
    if (draft.kind === "naryany") {
        return { operator: "naryany", question: draft.triggerKey };
    }
    if (!draft.optionKey) {
        return null;
    }
    return {
        operator: "naryvalue",
        question: draft.triggerKey,
        contains: draft.optionKey,
    };
}

function draftToRulesString(draft: RuleDraft): string | null {
    if (draft.conditions.length === 0) {
        return null;
    }
    if (draft.conditions.length === 1) {
        const leaf = draftToLeaf(draft.conditions[0]);
        return leaf ? serializeRule(leaf) : null;
    }
    const first = draftToLeaf(draft.conditions[0]);
    const second = draftToLeaf(draft.conditions[1]);
    if (!first) {
        return second ? serializeRule(second) : null;
    }
    if (!second) {
        return serializeRule(first);
    }
    return serializeRule(
        composeFlatTwoConditionRule({
            join: draft.join,
            first,
            second,
        }),
    );
}

interface ConditionRowProps {
    draft: LeafDraft;
    triggerOptions: BridgeEntityResult["questions"];
    promptsByTriggerKey: Map<string, BridgeEntityResult["questions"][number]["prompts"]>;
    onChange: (next: LeafDraft) => void;
    onRemove: () => void;
    editable: boolean;
}

const ConditionRow: React.FC<ConditionRowProps> = props => {
    const { draft, triggerOptions, promptsByTriggerKey, onChange, onRemove, editable } =
        props;

    const trigger = triggerOptions.find(q => q.key === draft.triggerKey);
    const triggerType = trigger?.questionType;

    const handleTriggerChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newTriggerKey = e.target.value;
            const newTrigger = triggerOptions.find(q => q.key === newTriggerKey);
            if (!newTrigger) {
                onChange({ ...draft, triggerKey: "", optionKey: "" });
                return;
            }
            const isBinary = newTrigger.questionType === BINARY_TYPE;
            onChange({
                kind: isBinary ? "binaryvalue" : "naryany",
                triggerKey: newTriggerKey,
                optionKey: "",
            });
        },
        [draft, triggerOptions, onChange],
    );

    const handleKindChange = useCallback(
        (value: string) => {
            if (value === "naryvalue" || value === "naryany") {
                onChange({ ...draft, kind: value, optionKey: "" });
            }
        },
        [draft, onChange],
    );

    const handleOptionChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            onChange({ ...draft, optionKey: e.target.value });
        },
        [draft, onChange],
    );

    const triggerSelectOptions = useMemo(() => {
        const list = triggerOptions.map(q => ({ key: q.key, label: q.title || q.key }));
        return [{ key: "", label: "— Select a question —" }, ...list];
    }, [triggerOptions]);

    const triggerPrompts = promptsByTriggerKey.get(draft.triggerKey) ?? [];

    return (
        <div className="border border-gray-200 rounded p-3 space-y-3 bg-gray-50">
            <Select
                label="Trigger question"
                value={draft.triggerKey}
                onChange={handleTriggerChange}
                disabled={!editable}>
                {triggerSelectOptions.map(opt => (
                    <option key={opt.key} value={opt.key}>
                        {opt.label}
                    </option>
                ))}
            </Select>
            {triggerType === BINARY_TYPE ? (
                <div className="text-sm text-gray-700">is answered Yes</div>
            ) : null}
            {triggerType === NARY_TYPE ? (
                <Radio
                    options={naryKindOptions}
                    value={draft.kind === "naryvalue" ? "naryvalue" : "naryany"}
                    label="Match"
                    onChange={handleKindChange}
                    disabled={!editable}
                />
            ) : null}
            {triggerType === NARY_TYPE && draft.kind === "naryvalue" ? (
                <Select
                    label="Option"
                    value={draft.optionKey}
                    onChange={handleOptionChange}
                    disabled={!editable}>
                    <option value="">— Select an option —</option>
                    {triggerPrompts
                        .filter(p => !!p.title)
                        .map(p => (
                            <option key={p.key} value={p.key}>
                                {p.title}
                            </option>
                        ))}
                </Select>
            ) : null}
            {editable ? (
                <SecondaryCompactButton type="button" onClick={onRemove}>
                    Remove condition
                </SecondaryCompactButton>
            ) : null}
        </div>
    );
};

interface QuestionConditionalInputProps {
    question: BridgeQuestionInput;
    entityMetadata: BridgeEntityResult;
    onChange: (q: BridgeQuestionInput) => void;
    editable: boolean;
}

export const QuestionConditionalInput: React.FC<
    QuestionConditionalInputProps
> = props => {
    const { question, entityMetadata, onChange, editable } = props;

    const [expanded, setExpanded] = useState(false);

    const parsedRule = useMemo(
        () => (question.rulesString ? parseRulesString(question.rulesString) : null),
        [question.rulesString],
    );
    const isInvalidSavedRule = !!question.rulesString && parsedRule === null;
    const isComplexSavedRule =
        !!parsedRule &&
        (parsedRule.operator === "and" || parsedRule.operator === "or") &&
        !isFlatTwoConditionRule(parsedRule);

    const initialDraft = useMemo<RuleDraft>(() => {
        if (!parsedRule) {
            return emptyDraft;
        }
        if (isComplexSavedRule) {
            return emptyDraft;
        }
        return ruleToDraft(parsedRule);
    }, [parsedRule, isComplexSavedRule]);

    const [draft, setDraft] = useState<RuleDraft>(initialDraft);

    const editableQuestionKey = question.key;

    const triggerOptions = useMemo(() => {
        const candidates = (entityMetadata.questions || []).filter(q => {
            if (q.id === question.id) {
                return false;
            }
            if (q.key === editableQuestionKey) {
                return false;
            }
            const isBinary = q.questionType === BINARY_TYPE;
            const isNary = q.questionType === NARY_TYPE;
            if (!isBinary && !isNary) {
                return false;
            }
            const candidateRule = q.rulesString ? parseRulesString(q.rulesString) : null;
            const wouldCycle = candidateDependsOnEditing({
                candidateRule,
                editingKey: editableQuestionKey,
                allQuestions: (entityMetadata.questions || []).map(other => ({
                    key: other.key,
                    rulesString: other.rulesString ?? null,
                })),
            });
            return !wouldCycle;
        });
        return candidates;
    }, [entityMetadata.questions, question.id, editableQuestionKey]);

    const promptsByTriggerKey = useMemo(() => {
        const map = new Map<string, BridgeEntityResult["questions"][number]["prompts"]>();
        for (const q of entityMetadata.questions || []) {
            map.set(q.key, q.prompts || []);
        }
        return map;
    }, [entityMetadata.questions]);

    const emit = useCallback(
        (next: RuleDraft) => {
            setDraft(next);
            onChange({ ...question, rulesString: draftToRulesString(next) });
        },
        [onChange, question],
    );

    const handleAddCondition = useCallback(() => {
        emit({ ...draft, conditions: [{ ...newCondition }] });
    }, [draft, emit]);

    const handleAddSecond = useCallback(() => {
        if (draft.conditions.length !== 1) {
            return;
        }
        emit({
            ...draft,
            conditions: [draft.conditions[0], { ...newCondition }],
        });
    }, [draft, emit]);

    const handleRemoveCondition = useCallback(
        (index: number) => {
            const remaining = draft.conditions.filter((_, i) => i !== index);
            emit({ ...draft, conditions: remaining });
        },
        [draft, emit],
    );

    const handleConditionChange = useCallback(
        (index: number, leaf: LeafDraft) => {
            const next = draft.conditions.map((c, i) => (i === index ? leaf : c));
            emit({ ...draft, conditions: next });
        },
        [draft, emit],
    );

    const handleJoinChange = useCallback(
        (value: string) => {
            if (value === "and" || value === "or") {
                emit({ ...draft, join: value });
            }
        },
        [draft, emit],
    );

    const handleClear = useCallback(() => {
        setDraft(emptyDraft);
        onChange({ ...question, rulesString: null });
    }, [onChange, question]);

    const headerToggle = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    const summary = useMemo<string | null>(() => {
        if (isInvalidSavedRule) {
            return "Invalid condition";
        }
        if (isComplexSavedRule) {
            return "Complex condition";
        }
        if (draft.conditions.length === 0) {
            return null;
        }
        if (draft.conditions.length === 1) {
            return "1 condition";
        }
        return `2 conditions (${draft.join.toUpperCase()})`;
    }, [isInvalidSavedRule, isComplexSavedRule, draft]);

    return (
        <div className="border-t border-gray-200 pt-4">
            <div
                onClick={headerToggle}
                className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-2 -m-2">
                <div className="flex items-center space-x-3">
                    <span className="font-medium">Advanced</span>
                    {summary ? (
                        <span className="text-xs text-gray-500">{summary}</span>
                    ) : null}
                </div>
                <ExpandIndicator expanded={expanded} />
            </div>
            {expanded ? (
                <div className="mt-4 space-y-4">
                    {isInvalidSavedRule ? (
                        <div className="space-y-2">
                            <InlineBanner type="error">
                                This question&apos;s saved condition is invalid.
                            </InlineBanner>
                            {editable ? (
                                <SecondaryCompactButton
                                    type="button"
                                    onClick={handleClear}>
                                    Clear condition
                                </SecondaryCompactButton>
                            ) : null}
                        </div>
                    ) : null}
                    {isComplexSavedRule ? (
                        <div className="space-y-2">
                            <InlineBanner type="info">
                                This question has a complex condition that can&apos;t be
                                edited here.
                            </InlineBanner>
                            {editable ? (
                                <SecondaryCompactButton
                                    type="button"
                                    onClick={handleClear}>
                                    Clear condition
                                </SecondaryCompactButton>
                            ) : null}
                        </div>
                    ) : null}
                    {!isInvalidSavedRule && !isComplexSavedRule ? (
                        <div className="space-y-3">
                            <div className="text-sm text-gray-700">
                                Show this question only when…
                            </div>
                            {draft.conditions.length === 0 ? (
                                editable ? (
                                    <SecondaryCompactButton
                                        type="button"
                                        onClick={handleAddCondition}>
                                        Add a condition
                                    </SecondaryCompactButton>
                                ) : null
                            ) : null}
                            {draft.conditions.map((leaf, index) => (
                                <React.Fragment key={index}>
                                    {index === 1 ? (
                                        <Radio
                                            options={joinOptions}
                                            value={draft.join}
                                            label="Combine with"
                                            onChange={handleJoinChange}
                                            disabled={!editable}
                                        />
                                    ) : null}
                                    <ConditionRow
                                        draft={leaf}
                                        triggerOptions={triggerOptions}
                                        promptsByTriggerKey={promptsByTriggerKey}
                                        onChange={next =>
                                            handleConditionChange(index, next)
                                        }
                                        onRemove={() => handleRemoveCondition(index)}
                                        editable={editable}
                                    />
                                </React.Fragment>
                            ))}
                            {editable && draft.conditions.length === 1 ? (
                                <SecondaryCompactButton
                                    type="button"
                                    onClick={handleAddSecond}>
                                    Add another condition
                                </SecondaryCompactButton>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
